import type { Column } from "@tanstack/react-table"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import DropdownMenu from "@/v2-ui/dropdownMenu"
import { useTableContext } from "../table.context"
import { getColumnTitle } from "../table.utils"
import {
  SUMMARY_ARITHMETIC_AGGREGATIONS_BY_KIND,
  SUMMARY_COUNT_AGGREGATIONS,
  SUMMARY_NONE,
} from "./table.summary.constants"
import type { SummaryAggregationType } from "./table.summary.types"
import { computeSummary, isSummaryAggregationType } from "./table.summary.utils"
import { useSummaryValues } from "./table.summary.values"
import TableSummaryVisual from "./TableSummaryVisual"

export type TableSummaryMenuProps<TData = unknown> = {
  column: Column<TData, unknown>
  /** The calculation the cell shows; `null` when it shows none. */
  value: SummaryAggregationType | null
  onChange(type: SummaryAggregationType | null): void
}

/**
 * How many rows the answers cover. On a server-paginated list that is still
 * being fetched, that is fewer than the view holds, and the title says so —
 * "1 000 of 3 252 rows" — until the rest has landed.
 */
function formatRowCoverage(covered: number, total: number | null): string {
  const rows = covered === 1 ? "row" : "rows"
  if (total !== null && total > covered) {
    return `${formatNumber(covered)} of ${formatNumber(total)} ${rows}`
  }
  return `${formatNumber(covered)} ${rows}`
}

/**
 * The calculations a column offers, each with its answer alongside — and its
 * bar, where the answer has one — so the operator reads them all at a glance
 * and pins the one worth keeping. The dropdown mounts this only while open,
 * so the previews cost nothing until asked for.
 */
function TableSummaryMenu<TData = unknown>(
  props: TableSummaryMenuProps<TData>,
) {
  const { column, value, onChange } = props
  const { countMeta } = useTableContext<TData>()
  const { values, kind } = useSummaryValues(column)
  const arithmeticTypes = SUMMARY_ARITHMETIC_AGGREGATIONS_BY_KIND[kind]

  function renderItem(type: SummaryAggregationType) {
    const result = computeSummary({ values, type, kind })
    return (
      <DropdownMenu.RadioItem
        key={type}
        value={type}
        className="ui-table-summary-menu-item"
      >
        <span className="ui-table-summary-menu-item-label">{result.label}</span>
        <span className="ui-table-summary-menu-item-preview">
          {result.visual && (
            <TableSummaryVisual
              visual={result.visual}
              className="ui-table-summary-menu-item-visual"
            />
          )}
          <span className="ui-table-summary-menu-item-preview-text">
            {result.value}
          </span>
        </span>
      </DropdownMenu.RadioItem>
    )
  }

  return (
    <>
      <DropdownMenu.Label className="ui-table-summary-menu-title">
        <span className="ui-table-summary-menu-title-column">
          {getColumnTitle(column)}
        </span>
        <span className="ui-table-summary-menu-title-rows">
          {formatRowCoverage(values.length, countMeta.total)}
        </span>
      </DropdownMenu.Label>
      <DropdownMenu.RadioGroup
        value={value ?? SUMMARY_NONE}
        onValueChange={(next) => {
          onChange(isSummaryAggregationType(next) ? next : null)
        }}
      >
        <DropdownMenu.RadioItem value={SUMMARY_NONE}>
          None
        </DropdownMenu.RadioItem>
        {arithmeticTypes.length > 0 && (
          <>
            <DropdownMenu.Separator />
            {arithmeticTypes.map(renderItem)}
          </>
        )}
        <DropdownMenu.Separator />
        {SUMMARY_COUNT_AGGREGATIONS.map(renderItem)}
      </DropdownMenu.RadioGroup>
    </>
  )
}

export default TableSummaryMenu
